import { useForm } from "@tanstack/react-form";
import {
	createFileRoute,
	Link,
	stripSearchParams,
	useRouteContext,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import {
	graphql,
	useFragment,
	useLazyLoadQuery,
	useMutation,
} from "react-relay";
import { toast } from "sonner";
import z from "zod";
import { Icon } from "@/components/icon";
import { Image } from "@/components/image";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { CardTitle } from "@/components/ui/card";
import {
	Field,
	FieldDescription,
	FieldError,
	FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useFileUpload } from "@/hooks/useFileUpload";
import { cn } from "@/utils";
import type { personalizacao_appearance$key } from "./__generated__/personalizacao_appearance.graphql";
import type { personalizacao_configuration$key } from "./__generated__/personalizacao_configuration.graphql";
import type { personalizacao_pages$key } from "./__generated__/personalizacao_pages.graphql";
import type { personalizacao_privacy$key } from "./__generated__/personalizacao_privacy.graphql";
import type { personalizacaoAppearanceMutation } from "./__generated__/personalizacaoAppearanceMutation.graphql";
import type { personalizacaoConfigurationMutation } from "./__generated__/personalizacaoConfigurationMutation.graphql";
import type { personalizacaoPagesMutation } from "./__generated__/personalizacaoPagesMutation.graphql";
import type { personalizacaoPrivacyMutation } from "./__generated__/personalizacaoPrivacyMutation.graphql";
import type { personalizacaoQuery } from "./__generated__/personalizacaoQuery.graphql";

const defaultValues = {
	view: "configuracoes-basicas",
} as const;

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/area-de-membros/personalizacao",
)({
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			view: z
				.enum(["configuracoes-basicas", "aparencia", "seo", "privacidade"])
				.default("configuracoes-basicas"),
		}),
	),
	search: {
		middlewares: [stripSearchParams(defaultValues)],
	},
});

function ConfigurationCard({
	club,
	clubId,
	clubSlug,
}: {
	club: personalizacao_configuration$key;
	clubId: string;
	clubSlug: string;
}) {
	const { session } = useRouteContext({ from: "__root__" });

	const data = useFragment<personalizacao_configuration$key>(
		graphql`
    fragment personalizacao_configuration on Club {
      name
      description
      logo {
        id
        signedUrl
      }
    }
  `,
		club,
	);

	const [isEditing, setIsEditing] = useState(false);
	const [isLogoHovering, setIsLogoHovering] = useState(false);

	const [updateClub] = useMutation<personalizacaoConfigurationMutation>(graphql`
		mutation personalizacaoConfigurationMutation($club: ID!, $payload: UpdateClubInput!) {
			updateClub(id: $club, payload: $payload) {
				id
				...personalizacao_configuration
			}
		}
	`);

	const { upload: uploadLogo } = useFileUpload({ club: clubSlug, session });

	const form = useForm({
		defaultValues: {
			name: data.name,
			...(data.description ? { description: data.description } : {}),
			...(data.logo
				? {
						logo: {
							id: data.logo.id!,
							url: data.logo.signedUrl!,
						},
					}
				: {}),
		},
		validators: {
			onChange: z.object({
				name: z
					.string()
					.min(3, "Nome precisa ter no mínimo 3 caracteres")
					.max(64, "Nome pode ter no máximo 64 caracteres"),
				description: z.string().optional(),
				logo: z
					.object({
						id: z.string(),
						url: z.url(),
					})
					.optional(),
			}),
		},
		onSubmit: ({ value }) => {
			return new Promise((resolve, reject) => {
				updateClub({
					variables: {
						club: clubId,
						payload: {
							name: value.name,
							description: value.description || null,
							logoFile: value.logo?.id || null,
						},
					},
					onCompleted: (response, errors) => {
						if (errors?.length) {
							toast.error(errors[0].message);
							reject(new Error(errors[0].message));
							return;
						}
						toast.success("Configurações atualizadas com sucesso!");
						setIsEditing(false);
						resolve(response);
					},
					onError: (error) => {
						toast.error("Erro ao atualizar configurações. Tente novamente.");
						reject(error);
					},
				});
			});
		},
	});

	const {
		getRootProps: getLogoRootProps,
		getInputProps: getLogoInputProps,
		open: openLogoPicker,
	} = useDropzone({
		noClick: true,
		accept: { "image/*": [] },
		disabled: !isEditing,
		onDrop: ([file]) => {
			if (file) {
				uploadLogo(file).then((res) => {
					form.setFieldValue("logo", { id: res.globalId, url: res.url });
				});
			}
		},
	});

	return (
		<form
			className="overflow-hidden rounded-md border py-6 px-6 flex flex-col gap-6"
			onSubmit={(e) => {
				e.preventDefault();
				e.stopPropagation();
				form.handleSubmit();
			}}
		>
			<div className="flex flex-row items-center justify-between">
				<CardTitle>Configurações Básicas</CardTitle>

				{isEditing ? (
					<form.Subscribe
						selector={(state) => [state.isSubmitting, state.canSubmit]}
					>
						{([isSubmitting, canSubmit]) => (
							<Button type="submit" disabled={isSubmitting || !canSubmit}>
								{isSubmitting && <Spinner />}
								Salvar
							</Button>
						)}
					</form.Subscribe>
				) : (
					<Button
						type="button"
						variant="outline"
						onClick={() => setIsEditing(true)}
					>
						<Icon name="Pencil" />
						Editar
					</Button>
				)}
			</div>

			<div className="w-full pt-3 flex flex-col gap-6">
				<form.Field name="logo">
					{(field) => (
						<div className="flex flex-row gap-3">
							<div className="flex flex-col flex-1">
								<span className="text-sm text-foreground font-medium">
									Avatar do Ecossistema
								</span>

								<span className="text-sm mt-1 text-muted-foreground">
									Dimensões Recomendadas: 256x256px
								</span>
							</div>

							{/* biome-ignore lint/a11y/noStaticElementInteractions: expected */}
							<div
								onMouseEnter={() => setIsLogoHovering(true)}
								onMouseLeave={() => setIsLogoHovering(false)}
								className="relative w-fit"
								{...getLogoRootProps()}
							>
								<input {...getLogoInputProps()} className="hidden" />
								<Avatar className="size-12 border border-dashed">
									<AvatarImage src={field.state.value?.url} />
									<AvatarFallback>
										{data.name.slice(0, 2).toUpperCase()}
									</AvatarFallback>
								</Avatar>
								{isEditing && isLogoHovering && (
									<button
										type="button"
										onClick={() => openLogoPicker()}
										className="inset-0 absolute size-12 flex items-center justify-center border border-primary rounded-full bg-primary/10 text-primary border-dashed"
									>
										<Icon name="Upload" className="size-5" />
									</button>
								)}
							</div>
						</div>
					)}
				</form.Field>

				<Separator />

				<form.Field name="name">
					{(field) => (
						<Field className="flex flex-col lg:flex-row lg:gap-6 lg:items-start lg:justify-between">
							<div className="flex flex-col gap-1.5">
								<FieldLabel>Nome do Ecossistema</FieldLabel>

								<FieldDescription>
									Nome que identifica seu ecossistema. Aparecerá na sua área de
									membros e em emails e outras comunicações.
								</FieldDescription>
							</div>

							<div className="flex flex-col gap-1.5 w-full max-w-md">
								<Input
									className="w-full"
									id={field.name}
									name={field.name}
									value={field.state.value}
									onBlur={field.handleBlur}
									onChange={(e) => field.handleChange(e.target.value)}
									placeholder="Nome do Ecossistema"
									disabled={!isEditing}
									aria-invalid={
										!!(field.state.meta.isTouched && field.state.meta.errors[0])
									}
								/>
								{field.state.meta.isTouched && field.state.meta.errors[0] ? (
									<FieldError errors={field.state.meta.errors} />
								) : null}
							</div>
						</Field>
					)}
				</form.Field>

				<Separator />

				<form.Field name="description">
					{(field) => (
						<Field className="flex flex-col lg:flex-row lg:gap-6 lg:items-start lg:justify-between">
							<div className="flex flex-col gap-1.5">
								<FieldLabel>Descrição do Ecossistema</FieldLabel>

								<FieldDescription>
									Explique o propósito do seu ecossistema e os benefícios para
									novos membros.
								</FieldDescription>
							</div>

							<div className="flex flex-col gap-1.5 w-full max-w-md">
								<Textarea
									className="w-full"
									id={field.name}
									name={field.name}
									value={field.state.value}
									onBlur={field.handleBlur}
									onChange={(e) => field.handleChange(e.target.value)}
									placeholder="Descrição do Ecossistema"
									disabled={!isEditing}
									aria-invalid={
										!!(field.state.meta.isTouched && field.state.meta.errors[0])
									}
								/>
								{field.state.meta.isTouched && field.state.meta.errors[0] ? (
									<FieldError errors={field.state.meta.errors} />
								) : null}
							</div>
						</Field>
					)}
				</form.Field>
			</div>
		</form>
	);
}

function AppearanceCard({
	club,
	clubId,
	clubSlug,
}: {
	club: personalizacao_appearance$key;
	clubId: string;
	clubSlug: string;
}) {
	const { session } = useRouteContext({ from: "__root__" });

	const data = useFragment<personalizacao_appearance$key>(
		graphql`
     fragment personalizacao_appearance on Club {
       showPoweredLogo
       favicon {
         id
         signedUrl
       }
       previewImage {
         id
         signedUrl
       }
       themes {
         variant
         styles
       }
     }
   `,
		club,
	);

	const [isEditing, setIsEditing] = useState(false);
	const [isFaviconHovering, setIsFaviconHovering] = useState(false);
	const [isPreviewHovering, setIsPreviewHovering] = useState(false);

	const [updateClub] = useMutation<personalizacaoAppearanceMutation>(graphql`
		mutation personalizacaoAppearanceMutation($club: ID!, $payload: UpdateClubInput!) {
			updateClub(id: $club, payload: $payload) {
				id
				...personalizacao_appearance
			}
		}
	`);

	const form = useForm({
		defaultValues: {
			...(data.favicon
				? {
						favicon: {
							id: data.favicon.id!,
							url: data.favicon.signedUrl!,
						},
					}
				: {}),
			...(data.previewImage
				? {
						previewImage: {
							id: data.previewImage.id!,
							url: data.previewImage.signedUrl!,
						},
					}
				: {}),
			showPoweredLogo: data.showPoweredLogo ?? true,
		},
		validators: {
			onChange: z.object({
				favicon: z
					.object({
						id: z.string(),
						url: z.url(),
					})
					.optional(),
				previewImage: z
					.object({
						id: z.string(),
						url: z.url(),
					})
					.optional(),
				showPoweredLogo: z.boolean(),
			}),
		},
		onSubmit: ({ value }) => {
			return new Promise((resolve, reject) => {
				updateClub({
					variables: {
						club: clubId,
						payload: {
							favicon: value.favicon?.id || null,
							previewImage: value.previewImage?.id || null,
							configurations: [
								{
									key: "SHOW_POWERED_LOGO",
									value: value.showPoweredLogo ? "1" : "0",
								},
							],
						},
					},
					onCompleted: (response, errors) => {
						if (errors?.length) {
							toast.error(errors[0].message);
							reject(new Error(errors[0].message));
							return;
						}
						toast.success("Aparência atualizada com sucesso!");
						setIsEditing(false);
						resolve(response);
					},
					onError: (error) => {
						toast.error("Erro ao atualizar aparência. Tente novamente.");
						reject(error);
					},
				});
			});
		},
	});

	const { upload: uploadFavicon } = useFileUpload({ club: clubSlug, session });
	const { upload: uploadPreview } = useFileUpload({ club: clubSlug, session });

	const {
		getRootProps: getFaviconRootProps,
		getInputProps: getFaviconInputProps,
		open: openFaviconPicker,
	} = useDropzone({
		noClick: true,
		accept: { "image/*": [] },
		disabled: !isEditing,
		onDrop: ([file]) => {
			if (file) {
				uploadFavicon(file).then((res) => {
					form.setFieldValue("favicon", { id: res.globalId, url: res.url });
				});
			}
		},
	});

	const {
		getRootProps: getPreviewRootProps,
		getInputProps: getPreviewInputProps,
		open: openPreviewPicker,
	} = useDropzone({
		noClick: true,
		accept: { "image/*": [] },
		disabled: !isEditing,
		onDrop: ([file]) => {
			if (file) {
				uploadPreview(file).then((res) => {
					form.setFieldValue("previewImage", {
						id: res.globalId,
						url: res.url,
					});
				});
			}
		},
	});

	return (
		<form
			className="overflow-hidden rounded-md border py-6 px-6 flex flex-col gap-6"
			onSubmit={(e) => {
				e.preventDefault();
				e.stopPropagation();
				form.handleSubmit();
			}}
		>
			<div className="flex flex-row items-center justify-between">
				<CardTitle>Aparência</CardTitle>

				{isEditing ? (
					<form.Subscribe
						selector={(state) => [state.isSubmitting, state.canSubmit]}
					>
						{([isSubmitting, canSubmit]) => (
							<Button type="submit" disabled={isSubmitting || !canSubmit}>
								{isSubmitting && <Spinner />}
								Salvar
							</Button>
						)}
					</form.Subscribe>
				) : (
					<Button
						type="button"
						variant="outline"
						onClick={() => setIsEditing(true)}
					>
						<Icon name="Pencil" />
						Editar
					</Button>
				)}
			</div>

			<div className="w-full pt-3 flex flex-col gap-6">
				<form.Field name="favicon">
					{(field) => (
						<Field className="flex flex-col lg:flex-row lg:gap-6 lg:items-center lg:justify-between">
							<div className="flex flex-col gap-1.5">
								<FieldLabel>Favicon</FieldLabel>

								<FieldDescription>
									Ícone que aparece na aba do navegador. Dimensões Recomendadas:
									32x32px ou 64x64px
								</FieldDescription>
							</div>

							<div className="w-full max-w-md flex flex-row items-center justify-end gap-3">
								{/* biome-ignore lint/a11y/noStaticElementInteractions: expected */}
								<div
									onMouseEnter={() => setIsFaviconHovering(true)}
									onMouseLeave={() => setIsFaviconHovering(false)}
									className="relative w-fit"
									{...getFaviconRootProps()}
								>
									<input {...getFaviconInputProps()} className="hidden" />
									<Avatar className="size-12 border border-dashed">
										<AvatarImage src={field.state.value?.url} />
										<AvatarFallback>ICO</AvatarFallback>
									</Avatar>
									{isEditing && isFaviconHovering && (
										<button
											type="button"
											onClick={() => openFaviconPicker()}
											className="inset-0 absolute size-12 flex items-center justify-center border border-primary rounded-full bg-primary/10 text-primary border-dashed"
										>
											<Icon name="Upload" className="size-5" />
										</button>
									)}
								</div>
							</div>
						</Field>
					)}
				</form.Field>

				<Separator />

				<form.Field name="previewImage">
					{(field) => (
						<Field className="flex flex-col lg:flex-row lg:gap-6 lg:items-start lg:justify-between">
							<div className="flex flex-col gap-1.5 items-start justify-start flex-1">
								<FieldLabel>Preview Social (OG Image)</FieldLabel>

								<FieldDescription>
									Imagem de pré-visualização que aparece quando seu ecossistema
									é compartilhado em redes sociais. Dimensões Recomendadas:
									1200x630px
								</FieldDescription>
							</div>

							<div className="flex flex-col gap-3 justify-end h-[180px] w-[320px] max-w-[320px] shrink-0">
								{/* biome-ignore lint/a11y/noStaticElementInteractions: expected */}
								<div
									onMouseEnter={() => setIsPreviewHovering(true)}
									onMouseLeave={() => setIsPreviewHovering(false)}
									className="relative aspect-video "
									{...getPreviewRootProps()}
								>
									<input {...getPreviewInputProps()} className="hidden" />
									<div
										className={cn(
											"rounded-md border overflow-hidden border-dashed",
											!field.state.value?.url && "bg-muted p-8",
										)}
									>
										{field.state.value?.url ? (
											<img
												src={field.state.value.url}
												alt="Preview"
												className="w-full h-auto object-cover aspect-video max-w-xs"
											/>
										) : (
											<div className="flex flex-col items-center justify-center text-muted-foreground">
												<Icon name="ImagePlus" className="size-8" />
												<span className="text-sm mt-2">
													Arraste uma imagem ou clique
												</span>
											</div>
										)}
									</div>
									{isEditing && isPreviewHovering && (
										<button
											type="button"
											onClick={() => openPreviewPicker()}
											className="inset-0 absolute flex items-center justify-center border border-primary rounded-md bg-primary/10 text-primary border-dashed"
										>
											<Icon name="Upload" className="size-8" />
										</button>
									)}
								</div>
							</div>
						</Field>
					)}
				</form.Field>

				<Separator />

				<form.Field name="showPoweredLogo">
					{(field) => (
						<Field className="flex flex-col w-full lg:flex-row lg:gap-6 lg:items-center lg:justify-between">
							<div className="flex flex-col grow gap-3 flex-1">
								<FieldLabel>Powered by Logo</FieldLabel>

								<FieldDescription>
									Exibir o logo "Powered by Scarf" no barra de navegação da área
									de membros
								</FieldDescription>

								<div
									className={cn(
										"w-fit rounded-md border bg-muted px-3 py-1.5 text-muted-foreground text-xs",
										!field.state.value && "opacity-40",
									)}
								>
									<span className="flex items-center text-muted-foreground leading-none">
										Powered by{" "}
										<Image
											src={
												"https://storage.googleapis.com/scarf-club-assets/logos/Scarf-Full-Primary.svg"
											}
											className="ml-1.5 inline h-4"
										/>
									</span>
								</div>
							</div>

							<div className="max-w-md flex items-start justify-end">
								<Switch
									checked={field.state.value}
									disabled={!isEditing}
									onCheckedChange={(checked) => field.handleChange(checked)}
								/>
							</div>
						</Field>
					)}
				</form.Field>

				<Separator />

				<Field className="flex flex-col w-full lg:flex-row lg:gap-6 lg:items-start lg:justify-between">
					<div className="flex flex-col grow gap-1.5 flex-1">
						<FieldLabel>Design</FieldLabel>

						<FieldDescription>
							Configure a aparência e configurações básicas do seu ecossistema
						</FieldDescription>
					</div>

					<div className="max-w-72 w-full">
						<div className="border rounded-md p-6 flex flex-col gap-6">
							<div className="flex flex-row items-center justify-between">
								<span
									className="font-semibold"
									style={{ fontFamily: data.themes?.[0].styles["font-sans"] }}
								>
									{data.themes?.[0].variant || "LIGHT"}
								</span>

								<Link from={Route.fullPath} to="/c/$club/tema">
									<Button variant="outline">
										<Icon name="Pencil" />
										Editar
									</Button>
								</Link>
							</div>

							<div className="flex flex-row items-center justify-start">
								{[
									data.themes?.[0].styles.primary,
									data.themes?.[0].styles.accent,
									data.themes?.[0].styles.secondary,
									data.themes?.[0].styles.border,
								].map((d, index) => (
									<div
										className="size-6 rounded-full border border-gray-200"
										key={index}
										style={{
											backgroundColor: d,
											translate: index * -5,
										}}
									/>
								))}
							</div>
						</div>
					</div>
				</Field>
			</div>
		</form>
	);
}

function PagesCard({
	club,
	clubId,
}: {
	club: personalizacao_pages$key;
	clubId: string;
}) {
	const data = useFragment<personalizacao_pages$key>(
		graphql`
    fragment personalizacao_pages on Club {
      name

      initialPage: configuration(key: "INITIAL_PAGE")
      coursePageTitle: configuration(key: "COURSE_PAGE_TITLE")
      communityPageTitle: configuration(key: "COMMUNITY_PAGE_TITLE")
      eventsPageTitle: configuration(key: "EVENTS_PAGE_TITLE")
      leaderboardPageTitle: configuration(key: "LEADERBOARD_PAGE_TITLE")
      coursesEnabled: configuration(key: "COURSES_ENABLED")
      communityEnabled: configuration(key: "COMMUNITY_ENABLED")
      eventsEnabled: configuration(key: "EVENTS_ENABLED")
      leaderboard: configuration(key: "LEADERBOARD")
    }
  `,
		club,
	);

	const [isEditing, setIsEditing] = useState(false);

	const [updateClub] = useMutation<personalizacaoPagesMutation>(graphql`
		mutation personalizacaoPagesMutation($club: ID!, $payload: UpdateClubInput!) {
			updateClub(id: $club, payload: $payload) {
				id
				...personalizacao_pages
			}
		}
	`);

	const form = useForm({
		defaultValues: {
			initialPage: data.initialPage || "COURSES",
			coursePageTitle: data.coursePageTitle || "Cursos",
			communityPageTitle: data.communityPageTitle || "Comunidade",
			eventsPageTitle: data.eventsPageTitle || "Eventos",
			leaderboardPageTitle: data.leaderboardPageTitle || "Ranking",
			coursesEnabled: data.coursesEnabled !== "0",
			communityEnabled: data.communityEnabled !== "0",
			eventsEnabled: data.eventsEnabled !== "0",
			leaderboardEnabled:
				data.leaderboard !== "DISABLED" && data.leaderboard !== "0",
		},
		validators: {
			onChange: z.object({
				initialPage: z.enum(["COURSES", "COMMUNITY", "EVENTS", "LEADERBOARD"]),
				coursePageTitle: z
					.string()
					.min(1, "Nome precisa de pelo menos 1 caractere"),
				communityPageTitle: z
					.string()
					.min(1, "Nome precisa de pelo menos 1 caractere"),
				eventsPageTitle: z
					.string()
					.min(1, "Nome precisa de pelo menos 1 caractere"),
				leaderboardPageTitle: z
					.string()
					.min(1, "Nome precisa de pelo menos 1 caractere"),
				coursesEnabled: z.boolean(),
				communityEnabled: z.boolean(),
				eventsEnabled: z.boolean(),
				leaderboardEnabled: z.boolean(),
			}),
		},
		onSubmit: ({ value }) => {
			return new Promise((resolve, reject) => {
				updateClub({
					variables: {
						club: clubId,
						payload: {
							configurations: [
								{ key: "INITIAL_PAGE", value: value.initialPage },
								{ key: "COURSE_PAGE_TITLE", value: value.coursePageTitle },
								{
									key: "COMMUNITY_PAGE_TITLE",
									value: value.communityPageTitle,
								},
								{ key: "EVENTS_PAGE_TITLE", value: value.eventsPageTitle },
								{
									key: "LEADERBOARD_PAGE_TITLE",
									value: value.leaderboardPageTitle,
								},
								{
									key: "COURSES_ENABLED",
									value: value.coursesEnabled ? "1" : "0",
								},
								{
									key: "COMMUNITY_ENABLED",
									value: value.communityEnabled ? "1" : "0",
								},
								{
									key: "EVENTS_ENABLED",
									value: value.eventsEnabled ? "1" : "0",
								},
								{
									key: "LEADERBOARD",
									value: value.leaderboardEnabled ? "ENABLED" : "DISABLED",
								},
							],
						},
					},
					onCompleted: (response, errors) => {
						if (errors?.length) {
							toast.error(errors[0].message);
							reject(new Error(errors[0].message));
							return;
						}
						toast.success("Configurações de páginas atualizadas com sucesso!");
						setIsEditing(false);
						resolve(response);
					},
					onError: (error) => {
						toast.error("Erro ao atualizar configurações. Tente novamente.");
						reject(error);
					},
				});
			});
		},
	});

	return (
		<form
			className="overflow-hidden rounded-md border py-6 px-6 flex flex-col gap-6"
			onSubmit={(e) => {
				e.preventDefault();
				e.stopPropagation();
				form.handleSubmit();
			}}
		>
			<div className="flex flex-row items-center justify-between">
				<CardTitle>Páginas</CardTitle>

				{isEditing ? (
					<form.Subscribe
						selector={(state) => [state.isSubmitting, state.canSubmit]}
					>
						{([isSubmitting, canSubmit]) => (
							<Button type="submit" disabled={isSubmitting || !canSubmit}>
								{isSubmitting && <Spinner />}
								Salvar
							</Button>
						)}
					</form.Subscribe>
				) : (
					<Button
						type="button"
						variant="outline"
						onClick={() => setIsEditing(true)}
					>
						<Icon name="Pencil" />
						Editar
					</Button>
				)}
			</div>

			<Field className="flex flex-col w-full lg:flex-row lg:gap-6 lg:items-start lg:justify-between">
				<div className="flex flex-col grow gap-1.5 flex-1">
					<FieldLabel>Telas Ativas</FieldLabel>

					<FieldDescription>
						Ative ou desative as telas da sua área de membros. Telas desativadas
						não serão exibidas para os membros.
					</FieldDescription>
				</div>

				<div className="flex flex-col gap-3 max-w-md w-full">
					<form.Field name="coursesEnabled">
						{(field) => (
							<div className="flex items-center justify-between gap-4 rounded-md border p-3">
								<span className="text-sm font-medium">Cursos</span>
								<Switch
									checked={field.state.value}
									disabled={!isEditing}
									onCheckedChange={(checked) => field.handleChange(checked)}
								/>
							</div>
						)}
					</form.Field>

					<form.Field name="communityEnabled">
						{(field) => (
							<div className="flex items-center justify-between gap-4 rounded-md border p-3">
								<span className="text-sm font-medium">Comunidade</span>
								<Switch
									checked={field.state.value}
									disabled={!isEditing}
									onCheckedChange={(checked) => field.handleChange(checked)}
								/>
							</div>
						)}
					</form.Field>

					<form.Field name="eventsEnabled">
						{(field) => (
							<div className="flex items-center justify-between gap-4 rounded-md border p-3">
								<span className="text-sm font-medium">Eventos</span>
								<Switch
									checked={field.state.value}
									disabled={!isEditing}
									onCheckedChange={(checked) => field.handleChange(checked)}
								/>
							</div>
						)}
					</form.Field>

					<form.Field name="leaderboardEnabled">
						{(field) => (
							<div className="flex items-center justify-between gap-4 rounded-md border p-3">
								<span className="text-sm font-medium">Ranking</span>
								<Switch
									checked={field.state.value}
									disabled={!isEditing}
									onCheckedChange={(checked) => field.handleChange(checked)}
								/>
							</div>
						)}
					</form.Field>
				</div>
			</Field>

			<Separator />

			<Field className="flex flex-col w-full lg:flex-row lg:gap-6 lg:items-start lg:justify-between">
				<div className="flex flex-col grow gap-1.5 flex-1">
					<FieldLabel>Título das Páginas</FieldLabel>

					<FieldDescription>
						Personalize o nome de cada página da sua comunidade e selecione a
						página inicial
					</FieldDescription>
				</div>

				<div className="grid grid-cols-2 gap-3 max-w-md">
					<form.Field name="coursePageTitle">
						{(field) => (
							<>
								<div className="text-sm text-right flex items-center justify-end">
									Cursos:
								</div>

								<div className="flex flex-col gap-1">
									<Input
										placeholder="Cursos"
										id={field.name}
										name={field.name}
										value={field.state.value}
										onBlur={field.handleBlur}
										onChange={(e) => field.handleChange(e.target.value)}
										disabled={!isEditing}
										aria-invalid={
											!!(
												field.state.meta.isTouched && field.state.meta.errors[0]
											)
										}
									/>
									{field.state.meta.isTouched && field.state.meta.errors[0] ? (
										<FieldError errors={field.state.meta.errors} />
									) : null}
								</div>
							</>
						)}
					</form.Field>

					<form.Field name="communityPageTitle">
						{(field) => (
							<>
								<div className="text-sm text-right flex items-center justify-end">
									Comunidade:
								</div>

								<div className="flex flex-col gap-1">
									<Input
										placeholder="Comunidade"
										id={field.name}
										name={field.name}
										value={field.state.value}
										onBlur={field.handleBlur}
										onChange={(e) => field.handleChange(e.target.value)}
										disabled={!isEditing}
										aria-invalid={
											!!(
												field.state.meta.isTouched && field.state.meta.errors[0]
											)
										}
									/>
									{field.state.meta.isTouched && field.state.meta.errors[0] ? (
										<FieldError errors={field.state.meta.errors} />
									) : null}
								</div>
							</>
						)}
					</form.Field>

					<form.Field name="eventsPageTitle">
						{(field) => (
							<>
								<div className="text-sm text-right flex items-center justify-end">
									Eventos:
								</div>

								<div className="flex flex-col gap-1">
									<Input
										placeholder="Eventos"
										id={field.name}
										name={field.name}
										value={field.state.value}
										onBlur={field.handleBlur}
										onChange={(e) => field.handleChange(e.target.value)}
										disabled={!isEditing}
										aria-invalid={
											!!(
												field.state.meta.isTouched && field.state.meta.errors[0]
											)
										}
									/>
									{field.state.meta.isTouched && field.state.meta.errors[0] ? (
										<FieldError errors={field.state.meta.errors} />
									) : null}
								</div>
							</>
						)}
					</form.Field>

					<form.Field name="leaderboardPageTitle">
						{(field) => (
							<>
								<div className="text-sm text-right flex items-center justify-end">
									Ranking:
								</div>

								<div className="flex flex-col gap-1">
									<Input
										placeholder="Ranking"
										id={field.name}
										name={field.name}
										value={field.state.value}
										onBlur={field.handleBlur}
										onChange={(e) => field.handleChange(e.target.value)}
										disabled={!isEditing}
										aria-invalid={
											!!(
												field.state.meta.isTouched && field.state.meta.errors[0]
											)
										}
									/>
									{field.state.meta.isTouched && field.state.meta.errors[0] ? (
										<FieldError errors={field.state.meta.errors} />
									) : null}
								</div>
							</>
						)}
					</form.Field>
				</div>
			</Field>

			<Separator />

			<form.Subscribe
				selector={(state) => ({
					coursesEnabled: state.values.coursesEnabled,
					communityEnabled: state.values.communityEnabled,
					eventsEnabled: state.values.eventsEnabled,
					leaderboardEnabled: state.values.leaderboardEnabled,
					initialPage: state.values.initialPage,
				})}
			>
				{(toggles) => {
					const allPages = [
						{
							label: "Curso",
							value: "COURSES",
							enabled: toggles.coursesEnabled,
						},
						{
							label: "Comunidade",
							value: "COMMUNITY",
							enabled: toggles.communityEnabled,
						},
						{
							label: "Eventos",
							value: "EVENTS",
							enabled: toggles.eventsEnabled,
						},
						{
							label: "Ranking",
							value: "LEADERBOARD",
							enabled: toggles.leaderboardEnabled,
						},
					];
					const enabledPages = allPages.filter((p) => p.enabled);

					// Auto-reset initialPage if the currently selected page is disabled
					const currentPageEnabled = allPages.find(
						(p) => p.value === toggles.initialPage,
					)?.enabled;
					if (!currentPageEnabled && enabledPages.length > 0) {
						form.setFieldValue("initialPage", enabledPages[0].value);
					}

					return (
						<form.Field name="initialPage">
							{(field) => (
								<Field className="flex flex-col w-full lg:flex-row lg:gap-6 lg:items-start lg:justify-between">
									<div className="flex flex-col grow gap-1.5 flex-1">
										<FieldLabel>Página Inicial</FieldLabel>

										<FieldDescription>
											Essa é a primeira página que os usuários verão quando
											acessarem sua área de membros.
										</FieldDescription>
									</div>

									<div className="max-w-sm flex items-center justify-end">
										<Select
											value={field.state.value}
											onValueChange={field.handleChange}
											disabled={!isEditing}
										>
											<SelectTrigger>
												<SelectValue placeholder="Selecione uma página" />
											</SelectTrigger>

											<SelectContent>
												{enabledPages.map((it) => (
													<SelectItem key={it.value} value={it.value}>
														{it.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
								</Field>
							)}
						</form.Field>
					);
				}}
			</form.Subscribe>
		</form>
	);
}

function PrivacyCard({
	club,
	clubId,
}: {
	club: personalizacao_privacy$key;
	clubId: string;
}) {
	const data = useFragment<personalizacao_privacy$key>(
		graphql`
     fragment personalizacao_privacy on Club {
       name
       visibility

       weeklyEmail: configuration(key: "WEEKLY_EMAIL")
     }
   `,
		club,
	);

	const [isEditing, setIsEditing] = useState(false);

	const [updateClub] = useMutation<personalizacaoPrivacyMutation>(graphql`
		mutation personalizacaoPrivacyMutation($club: ID!, $payload: UpdateClubInput!) {
			updateClub(id: $club, payload: $payload) {
				id
				...personalizacao_privacy
			}
		}
	`);

	const isPrivate = data.visibility === "PRIVATE";
	const isWeeklyEmailEnabled = !!(
		data.weeklyEmail === "ENABLED" || data.weeklyEmail === "1"
	);

	const form = useForm({
		defaultValues: {
			isPrivate,
			isWeeklyEmailEnabled,
		},
		onSubmit: ({ value }) => {
			return new Promise((resolve, reject) => {
				updateClub({
					variables: {
						club: clubId,
						payload: {
							visibility: value.isPrivate ? "PRIVATE" : "PUBLIC",
							configurations: [
								{
									key: "WEEKLY_EMAIL",
									value: value.isWeeklyEmailEnabled ? "ENABLED" : "DISABLED",
								},
							],
						},
					},
					onCompleted: (response, errors) => {
						if (errors?.length) {
							toast.error(errors[0].message);
							reject(new Error(errors[0].message));
							return;
						}
						toast.success(
							"Configurações de privacidade atualizadas com sucesso!",
						);
						setIsEditing(false);
						resolve(response);
					},
					onError: (error) => {
						toast.error("Erro ao atualizar configurações. Tente novamente.");
						reject(error);
					},
				});
			});
		},
	});

	return (
		<form
			className="overflow-hidden rounded-md border pt-6 flex flex-col"
			onSubmit={(e) => {
				e.preventDefault();
				e.stopPropagation();
				form.handleSubmit();
			}}
		>
			<div className="flex flex-row items-center justify-between px-6 mb-10">
				<CardTitle>Configurações de Privacidade</CardTitle>

				{isEditing ? (
					<form.Subscribe
						selector={(state) => [state.isSubmitting, state.canSubmit]}
					>
						{([isSubmitting, canSubmit]) => (
							<Button type="submit" disabled={isSubmitting || !canSubmit}>
								{isSubmitting && <Spinner />}
								Salvar
							</Button>
						)}
					</form.Subscribe>
				) : (
					<Button
						type="button"
						variant="outline"
						onClick={() => setIsEditing(true)}
					>
						<Icon name="Pencil" />
						Editar
					</Button>
				)}
			</div>

			<form.Field name="isPrivate">
				{(field) => (
					<Field className="flex flex-col px-6 w-full lg:flex-row lg:gap-6 lg:items-start lg:justify-between">
						<div className="flex flex-col grow gap-1.5 flex-1">
							<FieldLabel>Comunidade Privada</FieldLabel>

							<FieldDescription>
								Em comunidades privadas, apenas membros convidados por
								administrados podem acessá-las
							</FieldDescription>
						</div>

						<div className="max-w-md flex items-start justify-end">
							<Switch
								checked={field.state.value}
								disabled={!isEditing}
								onCheckedChange={(checked) => field.handleChange(checked)}
							/>
						</div>
					</Field>
				)}
			</form.Field>

			<form.Field name="isWeeklyEmailEnabled">
				{(field) => (
					<Field className="flex px-6 mt-6 mb-3 flex-col w-full lg:flex-row lg:gap-6 lg:items-start lg:justify-between">
						<div className="flex flex-col grow gap-1.5 flex-1">
							<FieldLabel>Enviar Resumo Semanal</FieldLabel>

							<FieldDescription>
								Enviaremos toda segunda feira um resumo com tudo que aconteceu
								na comunidade na semana anterior.
							</FieldDescription>
						</div>

						<div className="max-w-md flex items-start justify-end">
							<Switch
								checked={field.state.value}
								disabled={!isEditing}
								onCheckedChange={(checked) => field.handleChange(checked)}
							/>
						</div>
					</Field>
				)}
			</form.Field>

			<Link
				from={Route.fullPath}
				to="/c/$club/regras"
				className="flex px-6 flex-col py-3 w-full hover:bg-accent lg:flex-row lg:gap-6 lg:items-start lg:justify-between"
			>
				<div className="flex flex-col grow gap-1.5 flex-1">
					<FieldLabel>Regras da Comunidade</FieldLabel>

					<FieldDescription>
						Edite as regras da sua comunidade. Elas serão exibidas na página de
						criação de posts e os usuários deverão concordar com elas antes de
						ingressar na comunidade
					</FieldDescription>
				</div>

				<div className="max-w-md flex items-start justify-end">
					<Icon name="MoveUpRight" className="size-5 text-muted-foreground" />
				</div>
			</Link>

			<Link
				from={Route.fullPath}
				to="/c/$club/termos"
				className="flex flex-col px-6 py-3 w-full hover:bg-accent lg:flex-row lg:gap-6 lg:items-start lg:justify-between"
			>
				<div className="flex flex-col grow gap-1.5 flex-1">
					<FieldLabel>Termos de Uso</FieldLabel>

					<FieldDescription>
						Cadastre os termos de uso da sua comunidade. Os novos membros
						precisarão aceitar os termos antes de ingressar na comunidade.
					</FieldDescription>
				</div>

				<div className="max-w-md flex items-start justify-end mt-3">
					<Icon name="MoveUpRight" className="size-5 text-muted-foreground" />
				</div>
			</Link>
		</form>
	);
}

function RouteComponent() {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });

	const data = useLazyLoadQuery<personalizacaoQuery>(
		graphql`
    query personalizacaoQuery($slug: String!) {
      club(slug: $slug) {
        id
        ...personalizacao_configuration
        ...personalizacao_appearance
        ...personalizacao_pages
        ...personalizacao_privacy
      }
    }
  `,
		{ slug: club.slug },
	);

	return (
		<div className="p-6 flex flex-col gap-6">
			<ConfigurationCard
				club={data.club}
				clubId={data.club.id}
				clubSlug={club.slug}
			/>
			<AppearanceCard
				club={data.club}
				clubId={data.club.id}
				clubSlug={club.slug}
			/>
			<PagesCard club={data.club} clubId={data.club.id} />
			<PrivacyCard club={data.club} clubId={data.club.id} />
		</div>
	);
}
